/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  UiController,
  breakdownList,
  commands,
  compareWindows,
  consoleCommands,
  events,
  paceVariableNames,
} from 'modules/common/ui-controller';
import { expandFocusOnAll, getValuesFromList } from 'modules/common/utils/array';
import { KEYS } from 'modules/dashboard/constants';
import { dashboardActions } from 'modules/dashboard/slice';
import {
  getTimeFrameOption,
  setCompareWindowCommandValue,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import {
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
} from 'modules/common/ui-controller/utils/shared-command-steps';
import { iPreferActions } from '../slice';

// time given to the custom compare window to open before the custom dates are applied
const CUSTOM_COMPARE_WINDOW_DELAY = 2000;
// This tab is dormant: its key is not in TAB_CONFIG of the tab container, so these commands are
// not applied until the tab is enabled again
/**
 * I prefer tab filter commands in the order they are applied.
 * The position in this list is the execution order, see useCommandPipeline.
 * context is built in useIPreferCommandPipeline.
 */
const I_PREFER_FILTER_COMMAND_STEPS = [
  {
    command: consoleCommands.BOOKING_DATE,
    event: events.SET_IPREFER_CUSTOM_BOOKING,
    // open custom booking date picker, the date range is applied once it is opened
    prepare: (value, { dispatch }) => {
      if (value !== commands.OTB) {
        dispatch(iPreferActions.setIPreferOtb(true));
      }
    },
    ready: (context, { pending }) =>
      context.isLoaded &&
      (pending[consoleCommands.BOOKING_DATE] === commands.OTB || Boolean(context.customBooking)),
    apply: (value, { dispatch, resetBookingDate }) => {
      // checks if booking date range value is 'otb'
      if (value === commands.OTB) {
        dispatch(iPreferActions.setIPreferOtb(false));
        dispatch(iPreferActions.setIPreferBookingDate(resetBookingDate));
      } else {
        // emit event to set booking date range
        UiController.setBookingDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.SEGMENT,
    event: events.SET_IPREFER_BREAKDOWN,
    ready: (context) => context.isLoaded && context.chartBreakdownList?.length > 0,
    apply: (value, { dispatch, chartBreakdownList }) => {
      // get breakdown option value
      const breakdownOption = breakdownList[value?.substring(0, 2)];
      // get isDetailed state from command
      const detailsStatus = parseInt(value?.substring(2, 4), 10);
      if (breakdownOption !== undefined) {
        const option = chartBreakdownList?.filter((val) => val?.label?.includes(breakdownOption));
        if (option?.length > 0) {
          dispatch(iPreferActions.setIPreferChartBreakdown(option[0]?.id));
        }
        // set isDetailed  state
        if (!Number.isNaN(detailsStatus)) {
          dispatch(iPreferActions.setIPreferIsDetailed(detailsStatus === 1));
          dispatch(iPreferActions.setIPreferFocusOn([]));
        }
      }
    },
  },
  {
    command: consoleCommands.ALIGNMENT,
    event: events.IPREFER_ALIGNMENT,
    ready: (context) => context.isLoaded && Boolean(context.visualFilters),
    apply: (value, { handleToggleChange }) => {
      // set alignment toggle state command value
      handleToggleChange({ target: { checked: value } });
    },
  },
  {
    // custom dates are applied by the next step, only with the custom compare window
    command: consoleCommands.COMPARE_WINDOW,
    requires: [consoleCommands.CUSTOM_DATES],
    event: events.SET_IPREFER_COMPARE_WINDOW,
    ready: (context) => context.isLoaded && Boolean(context.dateComparison),
    apply: (value, { customBooking, handleComparisonChange }) => {
      // check if 'otb' value selected as compare window and custom booking is false
      setCompareWindowCommandValue(value, customBooking, handleComparisonChange);
      return compareWindows[value] === KEYS.CUSTOM
        ? [consoleCommands.COMPARE_WINDOW]
        : [consoleCommands.COMPARE_WINDOW, consoleCommands.CUSTOM_DATES];
    },
    delayAfter: (value) =>
      compareWindows[value] === KEYS.CUSTOM ? CUSTOM_COMPARE_WINDOW_DELAY : 0,
  },
  {
    command: consoleCommands.CUSTOM_DATES,
    event: events.SET_IPREFER_CUSTOM_DATE_RANGE,
    ready: (context, { keys }) =>
      context.isLoaded &&
      (context.dateComparison === KEYS.CUSTOM || !keys.includes(consoleCommands.COMPARE_WINDOW)),
    apply: (value, { dispatch, dateComparison }) => {
      // checks if date comparison value is 'custom'
      if (dateComparison === KEYS.CUSTOM) {
        const datesExtracted = value?.split('-');
        // reset custom dates
        dispatch(dashboardActions.setsIPreferCustomDate(datesExtracted[0]));
        // emit event to set custom date range
        UiController.setCustomDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.TIME_FRAME,
    event: events.SET_IPREFER_TIME_FRAME,
    ready: (context) => context.isLoaded && context.timeFrameList?.length > 0,
    apply: (value, { dispatch, timeFrameList }) => {
      // get time frame value form timeFrame list
      const timeFrameOption = getTimeFrameOption(timeFrameList, value);
      if (timeFrameOption) {
        dispatch(iPreferActions.setIPreferTimeFrame(timeFrameOption?.id));
      }
    },
  },
  {
    command: consoleCommands.METRIC_SELECTION,
    event: events.SET_IPREFER_METRIC_SELECTION,
    ready: (context) => context.isLoaded && context.metricSelectionList?.length > 0,
    apply: (value, { dispatch }) => {
      // get metric selection option form list
      const variable = paceVariableNames[value];
      if (variable !== undefined) {
        dispatch(iPreferActions.setIPreferMetricSelection(variable));
      }
    },
  },
  {
    command: consoleCommands.FOCUS,
    event: events.SET_IPREFER_FOCUS_ON,
    // wait until the focus on list belongs to the selected breakdown
    ready: (context) =>
      context.isLoaded && context.isFocusOnListReady && context.focusOnList?.length > 0,
    apply: (value, { dispatch, focusOnList }) => {
      // get selected focus on values from command value (ALL selects every option)
      const optionsSelected = getValuesFromList(expandFocusOnAll(value, focusOnList), focusOnList);
      dispatch(
        iPreferActions.setIPreferFocusOn(optionsSelected?.length > 0 ? optionsSelected : [])
      );
    },
  },
];

/**
 * I prefer tab commands of a command url in the order they are applied
 */
const I_PREFER_COMMAND_STEPS = [
  ...I_PREFER_FILTER_COMMAND_STEPS,
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
];

export default I_PREFER_COMMAND_STEPS;
